/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Common\FallbackAlert.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Composant d'alerte pour les fallbacks
 * Affiche des notifications visuelles lorsque des valeurs de fallback sont utilisées
 */

import React, { useState, useEffect } from 'react';
import FallbackNotificationService, { FallbackAlert as FallbackAlertType } from '../../services/FallbackNotificationService';

interface FallbackAlertProps {
  alert: FallbackAlertType;
  onDismiss?: () => void;
}

const FallbackAlert: React.FC<FallbackAlertProps> = ({ alert, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  // Fonction pour obtenir les styles selon la sévérité
  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          container: 'bg-red-50 border-red-200 border-l-4 border-l-red-500',
          icon: '💥',
          iconColor: 'text-red-500',
          textColor: 'text-red-800',
          buttonColor: 'bg-red-600 hover:bg-red-700 text-white',
          secondaryButtonColor: 'bg-red-100 hover:bg-red-200 text-red-800 border border-red-300'
        };
      case 'high':
        return {
          container: 'bg-orange-50 border-orange-200 border-l-4 border-l-orange-500',
          icon: '🔥',
          iconColor: 'text-orange-500',
          textColor: 'text-orange-800',
          buttonColor: 'bg-orange-600 hover:bg-orange-700 text-white',
          secondaryButtonColor: 'bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300'
        };
      case 'medium':
        return {
          container: 'bg-yellow-50 border-yellow-200 border-l-4 border-l-yellow-500',
          icon: '🚨',
          iconColor: 'text-yellow-500',
          textColor: 'text-yellow-800',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700 text-white',
          secondaryButtonColor: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-300'
        };
      case 'low':
        return {
          container: 'bg-blue-50 border-blue-200 border-l-4 border-l-blue-500',
          icon: '⚠️',
          iconColor: 'text-blue-500',
          textColor: 'text-blue-800',
          buttonColor: 'bg-blue-600 hover:bg-blue-700 text-white',
          secondaryButtonColor: 'bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300'
        };
      default:
        return {
          container: 'bg-gray-50 border-gray-200 border-l-4 border-l-gray-500',
          icon: '❓',
          iconColor: 'text-gray-500',
          textColor: 'text-gray-800',
          buttonColor: 'bg-gray-600 hover:bg-gray-700 text-white',
          secondaryButtonColor: 'bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300'
        };
    }
  };

  // Fonction pour obtenir le titre selon le type
  const getTypeTitle = (type: string) => {
    switch (type) {
      case 'tva':
        return 'Données TVA indisponibles';
      case 'fermetures':
        return 'Service fermetures indisponible';
      case 'mousses':
        return 'Service mousses indisponible';
      case 'matieres':
        return 'Matières serveur indisponibles';
      case 'clients':
        return 'Clients serveur indisponibles';
      case 'calcul':
        return 'Erreur de calcul serveur';
      case 'sauvegarde':
        return 'Échec sauvegarde serveur';
      case 'export':
        return 'Échec export serveur';
      default:
        return 'Problème système';
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (alert.onDismiss) {
        alert.onDismiss();
      }
      if (onDismiss) {
        onDismiss();
      }
    }, 300); // Délai pour l'animation
  };

  const handleRetry = () => {
    if (alert.onRetry) {
      alert.onRetry();
    }
  };

  const styles = getSeverityStyles(alert.severity);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`${styles.container} p-4 mb-4 rounded-md shadow-sm transition-all duration-300 ${isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-2'}`}>
      <div className="flex items-start">
        {/* Icône */}
        <div className="flex-shrink-0">
          <span className={`text-2xl ${styles.iconColor}`}>
            {styles.icon}
          </span>
        </div>

        {/* Contenu */}
        <div className="ml-3 flex-1">
          {/* Titre */}
          <h3 className={`text-sm font-semibold ${styles.textColor} mb-1`}>
            {getTypeTitle(alert.type)}
          </h3>

          {/* Message */}
          <p className={`text-sm ${styles.textColor} mb-2`}>
            {alert.message}
          </p>

          {/* Valeur de fallback */}
          {alert.fallbackValue && (
            <div className="mb-3">
              <span className={`text-xs ${styles.textColor} opacity-75`}>
                Valeur utilisée: 
              </span>
              <code className={`ml-1 px-2 py-1 text-xs rounded ${styles.secondaryButtonColor.replace('hover:bg-', 'bg-').replace('text-', '').replace('border border-', '')}`}>
                {alert.fallbackValue}
              </code>
            </div>
          )}

          {/* Contexte */}
          {alert.context && (
            <p className={`text-xs ${styles.textColor} opacity-75 mb-3`}>
              Contexte: {alert.context}
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2">
            {alert.onRetry && (
              <button
                type="button"
                onClick={handleRetry}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${styles.buttonColor} focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                Réessayer
              </button>
            )}
            
            <button
              type="button"
              onClick={handleDismiss}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${styles.secondaryButtonColor} focus:outline-none focus:ring-2 focus:ring-offset-2`}
            >
              Fermer
            </button>
          </div>
        </div>

        {/* Bouton fermer (X) */}
        <div className="flex-shrink-0 ml-4">
          <button
            type="button"
            onClick={handleDismiss}
            className={`inline-flex rounded-md p-1.5 ${styles.textColor} hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2`}
          >
            <span className="sr-only">Fermer</span>
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant conteneur pour toutes les alertes actives
export const FallbackAlertsContainer: React.FC = () => {
  const [alerts, setAlerts] = useState<FallbackAlertType[]>([]);

  useEffect(() => {
    // S'abonner aux alertes actives
    const unsubscribe = FallbackNotificationService.subscribeToAlerts(setAlerts);
    
    return unsubscribe;
  }, []);

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md w-full space-y-2">
      {alerts.map((alert, index) => (
        <FallbackAlert
          key={`${alert.type}-${index}`}
          alert={alert}
          onDismiss={() => {
            // L'alerte sera automatiquement supprimée par le service
          }}
        />
      ))}
    </div>
  );
};

// Composant toast simple pour les notifications non-critiques
interface FallbackToastProps {
  type: string;
  message: string;
  severity: string;
  onDismiss: () => void;
}

export const FallbackToast: React.FC<FallbackToastProps> = ({ type, message, severity, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Auto-dismiss après 5 secondes pour les toasts non-critiques
    if (severity !== 'critical') {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onDismiss, 300);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [severity, onDismiss]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return '💥';
      case 'high': return '🔥';
      case 'medium': return '🚨';
      case 'low': return '⚠️';
      default: return '❓';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className={`transform transition-all duration-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
        <div className="flex items-start">
          <div className={`flex-shrink-0 w-8 h-8 rounded-full ${getSeverityColor(severity)} flex items-center justify-center`}>
            <span className="text-white text-sm">
              {getSeverityIcon(severity)}
            </span>
          </div>
          
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-900">
              Fallback {type}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {message}
            </p>
          </div>
          
          <button
            onClick={() => {
              setIsVisible(false);
              setTimeout(onDismiss, 300);
            }}
            className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Indicateur visuel pour les champs affectés par un fallback
interface FallbackIndicatorProps {
  type: string;
  message?: string;
  fallbackValue?: string;
  className?: string;
}

export const FallbackIndicator: React.FC<FallbackIndicatorProps> = ({ 
  type, 
  message, 
  fallbackValue, 
  className = '' 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'tva': return '💰';
      case 'fermetures': return '🔧';
      case 'mousses': return '🧽';
      case 'matieres': return '🧵';
      case 'clients': return '👤';
      case 'calcul': return '🧮';
      default: return '⚠️';
    }
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <div
        className="flex items-center justify-center w-5 h-5 bg-yellow-100 border border-yellow-300 rounded-full cursor-help"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span className="text-xs">
          {getTypeIcon(type)}
        </span>
      </div>
      
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap z-10">
          <div className="font-medium">Valeur de fallback</div>
          {message && <div className="mt-1">{message}</div>}
          {fallbackValue && (
            <div className="mt-1">
              <span className="opacity-75">Valeur: </span>
              <code className="bg-gray-800 px-1 rounded">{fallbackValue}</code>
            </div>
          )}
          {/* Flèche du tooltip */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default FallbackAlert;
